import {
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { useFeedback, useFeedbackAttachments, useFeedbackSummary, useRecoveryCases, useUpdateRecoveryCase } from '@/api/hooks'
import { api } from '@/api/client'
import MicIcon from '@mui/icons-material/Mic'
import PhotoIcon from '@mui/icons-material/Photo'
import { useAuthStore } from '@/auth/authStore'
import type { RecoveryCase } from '@/api/types'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  humanise,
  MetricCard,
  PageHeader,
  StatusChip,
} from '@/components/common'

function FeedbackAttachmentsCell({ feedbackId }: { feedbackId: string }) {
  const { data } = useFeedbackAttachments(feedbackId)
  const [playingUrl, setPlayingUrl] = useState<string | null>(null)

  if (!data || data.length === 0) {
    return <Typography variant="caption" color="text.secondary">—</Typography>
  }

  const closePlayer = () => {
    if (playingUrl) URL.revokeObjectURL(playingUrl)
    setPlayingUrl(null)
  }

  return (
    <>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {data.map((attachment, index) => (
          <Chip
            key={attachment.documentId}
            size="small"
            icon={attachment.kind === 'VOICE' ? <MicIcon /> : <PhotoIcon />}
            label={attachment.kind === 'VOICE' ? 'Voice note' : `Photo ${index + 1}`}
            onClick={() =>
              attachment.kind === 'VOICE'
                ? void api.blobUrl(`/api/v1/documents/${attachment.documentId}`).then(setPlayingUrl)
                : void api.download(`/api/v1/documents/${attachment.documentId}`, undefined, 'photo')
            }
          />
        ))}
      </Stack>
      <Dialog open={Boolean(playingUrl)} onClose={closePlayer} maxWidth="xs" fullWidth>
        <DialogTitle>Voice note</DialogTitle>
        <DialogContent>
          {playingUrl ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio controls autoPlay src={playingUrl} style={{ width: '100%' }} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePlayer}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export function FeedbackPage() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const [caseStatus, setCaseStatus] = useState('')
  const [editing, setEditing] = useState<RecoveryCase | null>(null)

  const { data: summary } = useFeedbackSummary()
  const { data: feedback, isLoading, error } = useFeedback({ size: 25 })
  const { data: cases } = useRecoveryCases(caseStatus || undefined)

  const dimensions = summary
    ? [
        { label: 'Food quality', value: summary.foodQuality },
        { label: 'Taste', value: summary.taste },
        { label: 'Presentation', value: summary.presentation },
        { label: 'Service', value: summary.service },
        { label: 'Staff behaviour', value: summary.staffBehaviour },
        { label: 'Timeliness', value: summary.timeliness },
        { label: 'Cleanliness', value: summary.cleanliness },
        { label: 'Value for money', value: summary.valueForMoney },
        { label: 'Sales experience', value: summary.salesExperience },
      ].filter((entry) => entry.value !== undefined && entry.value !== null)
    : []

  return (
    <>
      <PageHeader
        title="Feedback"
        subtitle="Ratings by dimension, net promoter score and open service recovery cases"
      />
      <ErrorBanner error={error} />

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Overall rating"
              value={summary.averageOverall ? `${summary.averageOverall.toFixed(2)} / 5` : '—'}
              tone={summary.averageOverall && summary.averageOverall < 3.5 ? 'warning' : 'success'}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard label="Responses" value={formatNumber(summary.responseCount)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Net promoter score"
              value={summary.npsScore === null || summary.npsScore === undefined ? '—' : summary.npsScore}
              tone={summary.npsScore !== null && summary.npsScore !== undefined && summary.npsScore < 0 ? 'error' : 'default'}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Open recovery cases"
              value={formatNumber(summary.openRecoveryCases)}
              tone={summary.openRecoveryCases > 0 ? 'error' : 'success'}
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                By dimension
              </Typography>
              {dimensions.length === 0 ? (
                <EmptyState message="No feedback recorded yet." />
              ) : (
                <Stack spacing={1.5}>
                  {dimensions.map((entry) => (
                    <Stack
                      key={entry.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">{entry.label}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Rating value={entry.value ?? 0} precision={0.1} size="small" readOnly />
                        <Typography variant="caption" color="text.secondary">
                          {entry.value?.toFixed(2)}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            {isLoading && <LinearProgress />}
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Recent feedback
              </Typography>
              {!feedback || feedback.content.length === 0 ? (
                <EmptyState
                  message="No feedback yet."
                  hint="A request goes out automatically once an event is marked completed."
                />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Comments</TableCell>
                      <TableCell>Attached</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feedback.content.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>{entry.orderNumber}</TableCell>
                        <TableCell>{entry.customerName}</TableCell>
                        <TableCell>{formatDate(entry.submittedAt)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Rating value={entry.overallRating} precision={0.1} size="small" readOnly />
                            <Typography variant="caption">{entry.overallRating.toFixed(1)}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {entry.comments ?? '—'}
                        </TableCell>
                        <TableCell>
                          <FeedbackAttachmentsCell feedbackId={entry.id} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle1">Service recovery</Typography>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={caseStatus}
                  onChange={(event) => setCaseStatus(event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {['OPEN', 'IN_PROGRESS', 'AWAITING_CUSTOMER', 'RESOLVED', 'CLOSED'].map((value) => (
                    <MenuItem key={value} value={value}>
                      {humanise(value)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {!cases || cases.length === 0 ? (
                <EmptyState message="No service recovery cases. Every event is landing well." />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Case</TableCell>
                      <TableCell>Trigger</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell align="right">Compensation</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cases.map((recoveryCase) => (
                      <TableRow key={recoveryCase.id} hover>
                        <TableCell>{recoveryCase.caseNumber}</TableCell>
                        <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {recoveryCase.triggerReason ?? '—'}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={recoveryCase.severity} />
                        </TableCell>
                        <TableCell>
                          <StatusChip status={recoveryCase.status} />
                        </TableCell>
                        <TableCell>{formatDateTime(recoveryCase.dueAt)}</TableCell>
                        <TableCell align="right">
                          {formatMoney(recoveryCase.compensationAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {canWrite('feedback') && (
                            <Button size="small" onClick={() => setEditing(recoveryCase)}>
                              Update
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <RecoveryDialog recoveryCase={editing} onClose={() => setEditing(null)} />
    </>
  )
}

function RecoveryDialog({
  recoveryCase,
  onClose,
}: {
  recoveryCase: RecoveryCase | null
  onClose: () => void
}) {
  const update = useUpdateRecoveryCase()
  const [rootCause, setRootCause] = useState('')
  const [correctiveAction, setCorrectiveAction] = useState('')
  const [compensationAmount, setCompensationAmount] = useState('0')
  const [status, setStatus] = useState('IN_PROGRESS')

  return (
    <Dialog open={Boolean(recoveryCase)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update {recoveryCase?.caseNumber}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={update.error} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A case can only be closed once the corrective action is recorded and the customer has
          confirmed the resolution.
        </Typography>
        <Stack spacing={2}>
          <TextField
            select
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            {['OPEN', 'IN_PROGRESS', 'AWAITING_CUSTOMER', 'RESOLVED'].map((value) => (
              <MenuItem key={value} value={value}>
                {humanise(value)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Root cause"
            value={rootCause}
            onChange={(event) => setRootCause(event.target.value)}
            multiline
            minRows={2}
          />
          <TextField
            label="Corrective action"
            value={correctiveAction}
            onChange={(event) => setCorrectiveAction(event.target.value)}
            multiline
            minRows={2}
          />
          <TextField
            label="Compensation"
            type="number"
            value={compensationAmount}
            onChange={(event) => setCompensationAmount(event.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={update.isPending || !recoveryCase}
          onClick={async () => {
            if (!recoveryCase) return
            await update.mutateAsync({
              id: recoveryCase.id,
              body: {
                status,
                rootCause: rootCause || undefined,
                correctiveAction: correctiveAction || undefined,
                compensationAmount: Number(compensationAmount) || 0,
              },
            })
            onClose()
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
